/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useRef, useState, useEffect } from 'react'

import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/loader.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/dimmer.css'

import './styles.scss'

import { Waypoint } from 'react-waypoint'

import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Tracking } from 'lib/Tracking'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

import Cover from '../cover'
import Institution from '../institution'
import LeafletMap from '../leaflet-map'

import { sections } from 'lib/institutions'

// TODO Set the correct article ID here
const ARTICLE_ID = '509603917080'

export function AppComponent() {
  // layout
  const refContainer = useRef(null)
  const [isMobile, updateIsMobile] = useState(null)
  const [mapHeight, updateHeight] = useState(0)

  // scrollytelling
  const [item, updateItem] = useState('all')
  const [place, updatePlace] = useState(null)

  // "componentDidMount-effect"
  useEffect(() => {
    updateIsMobile(window.innerWidth < 800)
  }, [])

  useEffect(() => {
    const {
      height
    } = refContainer.current.refs.leafletMap.container.getBoundingClientRect()

    updateHeight(height)
  }, [])

  // waypoint callback for items
  function itemHandler(name, position, props, firstOrLast) {
    if (
      position === 'top' &&
      props.currentPosition === 'above' &&
      props.previousPosition === 'inside'
    ) {
      updateItem(name)
    }
    if (
      position === 'top' &&
      !!firstOrLast === true &&
      props.previousPosition === 'above' &&
      props.currentPosition === 'inside'
    ) {
      updateItem('all')
    }
    if (
      position === 'bottom' &&
      props.previousPosition === 'above' &&
      props.currentPosition === 'inside'
    ) {
      updateItem(name)
    }

    if (
      position === 'bottom' &&
      !!firstOrLast === true &&
      props.previousPosition === 'inside' &&
      props.currentPosition === 'above'
    ) {
      updateItem('all')
    }
  }

  return (
    <div className='index'>
      <Masthead
        articleId={ARTICLE_ID}
        mediaName='derbund'
        fullMediaName='Der Bund'
        homepage='https://www.derbund.ch'
      />
      <ThemeSwitch />

      <header className='ui vertical very relaxed segment'>
        <Cover
          title='Das Erbe der Berner 80er-Bewegung'
          subtitle='Wo sich die Ideen der «Unzufriedenen» etabliert haben – eine Übersicht'
        />
      </header>

      <Tracking eventAction='vertical-scroll' eventLabel='startReading' />

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='metainfo'>
            <div className='author'>
              <nobr>Fabian Christl (Auswahl)</nobr>,{' '}
              <nobr>Christian Zellweger (Umsetzung)</nobr>
            </div>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            Ein autonomes Jugendzentrum: Es war die erste Forderung der
            vorwiegend jungen Männer und Frauen, die ab 1980 Berns Strassen
            unsicher machten{' '}
            <em>
              (Lesen Sie an dieser Stelle zur Geschichte der 80er-Bewegung in
              Bern:{' '}
              <a href='https://www.derbund.ch/358162212865'>
                «Ein Pflasterstein, der die Bundesstadt verändert hat»
              </a>
            </em>
            ). Doch dahinter steckte mehr als einfach der Wunsch von
            Jugendlichen nach einem eigenen Raum, es beinhaltete die Ablehnung
            der herrschenden Strukturen, von Autorität, Hierarchie, Moral und
            Konsum.
          </p>
          <p>
            Doch es blieb nicht beim Njet zum Bestehenden: Viele Bewegte
            versuchten, eigene Strukturen zu schaffen, die der Weltanschauung
            besser entsprachen. So entstanden in den 80er-Jahren, teils direkt
            durch die Bewegung, teils in ihrem Dunste und Geiste, zahlreiche
            Projekte und Institutionen. Einige davon haben bis heute Bestand –
            und prägen die Bundesstadt noch immer.
          </p>

          <div className='ui icon message'>
            <i className='newspaper outline icon' />
            <div className='content'>
              <div className='header'>Was ist Ihre 80er-Geschichte?</div>
              <p>
                <i className='external alternate icon' />
                <a href='https://stadtgespraech.derbund.ch/?p=2391'>
                  Diskutieren Sie mit im «Stadtgespräch»
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className='ui text container'>
          {/*

            SECTION CULTURE

          */}
          <div className='item' id={sections.CULTURE}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.CULTURE}`}
            />
            <h2 className='item-title dividing ui infographic header'>
              Kunst und Kultur
            </h2>
            <p>
              Die grössten kulturellen Institutionen, die ihre Wurzeln in der
              Bewegung haben, sind die Reitschule und die Dampfzentrale. Daneben
              gab es aber gerade im künstlerisch und kreativen Bereich viele
              kleinere Zusammenschlüsse von Kunstschaffenden. Viele haben sich
              aufgelöst, einige existieren noch immer.
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.CULTURE}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* Reitschule */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('reitschule', 'top', props, 'first')
                }
                onLeave={props =>
                  itemHandler('reitschule', 'top', props, 'first')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='reitschule'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('reitschule', 'bottom', props)}
                onLeave={props => itemHandler('reitschule', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Dampfzentrale */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('dampfzentrale', 'top', props)}
                onLeave={props => itemHandler('dampfzentrale', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='dampfzentrale'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('dampfzentrale', 'bottom', props)}
                onLeave={props => itemHandler('dampfzentrale', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Club111 */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('club111', 'top', props)}
                onLeave={props => itemHandler('club111', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='club111'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('club111', 'bottom', props)}
                onLeave={props => itemHandler('club111', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Kunstkanal */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('kunstkanal', 'top', props)}
                onLeave={props => itemHandler('kunstkanal', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='kunstkanal'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('kunstkanal', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('kunstkanal', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>

          {/*

            SECTION ECONOMY

          */}
          <div className='item' id={sections.ECONOMY}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.ECONOMY}`}
            />
            <h2 className='dividing ui infographic header'>
              Alternativ-Wirtschaft
            </h2>
            <p>
              Viele Engagierte wollten sich nicht mit freien Kulturräumen
              begnügen, sondern träumten von Unternehmen im Kollektivbesitz,
              ohne Chef und mit Sinn für soziale Gerechtigkeit und Umweltschutz.
              So entstanden kollektiv geführte Beizen, Fair-Trade-Läden,
              genossenschaftlich geführte Handwerksbetriebe – aber auch
              Finanzdienstleister wie ein Treuhandbüro oder eine
              Vorsorgestiftung.
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.ECONOMY}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* Brasserie Lorraine */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('brasserie-lorraine', 'top', props, 'first')
                }
                onLeave={props =>
                  itemHandler('brasserie-lorraine', 'top', props, 'first')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='brasserie-lorraine'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('brasserie-lorraine', 'bottom', props)
                }
                onLeave={props =>
                  itemHandler('brasserie-lorraine', 'bottom', props)
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Velokurier */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('velokurier', 'top', props)}
                onLeave={props => itemHandler('velokurier', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='velokurier'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('velokurier', 'bottom', props)}
                onLeave={props => itemHandler('velokurier', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Manus Bau + Schreinerei */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('manus', 'top', props)}
                onLeave={props => itemHandler('manus', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='manus'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('manus', 'bottom', props)}
                onLeave={props => itemHandler('manus', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Velowerkstatt */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('velowerkstatt', 'top', props)}
                onLeave={props => itemHandler('velowerkstatt', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='velowerkstatt'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('velowerkstatt', 'bottom', props)}
                onLeave={props => itemHandler('velowerkstatt', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Hallerladen */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('hallerladen', 'top', props)}
                onLeave={props => itemHandler('hallerladen', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='hallerladen'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('hallerladen', 'bottom', props)}
                onLeave={props => itemHandler('hallerladen', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Weltladen */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('weltladen', 'top', props)}
                onLeave={props => itemHandler('weltladen', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='weltladen'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('weltladen', 'bottom', props)}
                onLeave={props => itemHandler('weltladen', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Wylereggladen */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('wylereggladen', 'top', props)}
                onLeave={props => itemHandler('wylereggladen', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='wylereggladen'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('wylereggladen', 'bottom', props)}
                onLeave={props => itemHandler('wylereggladen', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Treuhandbüro Tis */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('treuhandburoTis', 'top', props)}
                onLeave={props => itemHandler('treuhandburoTis', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='treuhandburoTis'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('treuhandburoTis', 'bottom', props)
                }
                onLeave={props =>
                  itemHandler('treuhandburoTis', 'bottom', props)
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Vorsorgestiftung Gepabu */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('vorsorgestiftungGepabu', 'top', props)
                }
                onLeave={props =>
                  itemHandler('vorsorgestiftungGepabu', 'top', props)
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='vorsorgestiftungGepabu'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('vorsorgestiftungGepabu', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('vorsorgestiftungGepabu', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>

          {/*

            SECTION HOOD

          */}
          <div className='item' id={sections.HOOD}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.HOOD}`}
            />
            <h2 className='dividing ui infographic header'>Quartierarbeit</h2>
            <p>
              Ein Spezifikum der 80er-Bewegung war der Fokus auf das
              Unmittelbare, das eigene Umfeld. So entstanden auch in den
              Quartieren und Nachbarschaften zahlreiche Initiativen, die etwa
              ein Quartierzentrum forderten oder sich für günstigen Wohnraum
              einsetzten.
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.HOOD}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* Breitsch-Träff */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('breitschTraff', 'top', props, 'first')
                }
                onLeave={props =>
                  itemHandler('breitschTraff', 'top', props, 'first')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='breitschTraff'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('breitschTraff', 'bottom', props)}
                onLeave={props => itemHandler('breitschTraff', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Villa Stucki */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('villaStucki', 'top', props)}
                onLeave={props => itemHandler('villaStucki', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='villaStucki'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('villaStucki', 'bottom', props)}
                onLeave={props => itemHandler('villaStucki', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Läbigi Lorraine */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('labigiLorraine', 'top', props)}
                onLeave={props => itemHandler('labigiLorraine', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='labigiLorraine'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('labigiLorraine', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('labigiLorraine', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>

          {/*

            SECTION HOUSING

          */}
          <div className='item' id={sections.HOUSING}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.HOUSING}`}
            />
            <h2 className='dividing ui infographic header'>Wohnen</h2>
            <p>
              Einer besonderen Bedeutung kommen in der 80er-Jahre auch grössere
              Wohngemeinschaften und besetzte Häuser zu. Sie dienten der Szene
              für Treffen, Vernetzungen und Partys, und sie erlaubten den
              Bewohnern, mit neuen Formen von Gemeinschaft jenseits der
              bürgerlichen Kleinfamilie zu experimentieren.
            </p>
            <p>
              Allerdings zermürbt die ständige Suche nach neuen leeren Häusern,
              so dass sich vermehrt Leute zusammen taten, und gemeinsam Häuser
              kauften – teils um den Abriss oder teure Sanierungen zu verhindern
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.HOUSING}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* WBG Giebel */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('giebel', 'top', props, 'first')}
                onLeave={props => itemHandler('giebel', 'top', props, 'first')}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='giebel'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('giebel', 'bottom', props)}
                onLeave={props => itemHandler('giebel', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Q-Hof */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('qHof', 'top', props)}
                onLeave={props => itemHandler('qHof', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='qHof'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('qHof', 'bottom', props)}
                onLeave={props => itemHandler('qHof', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Murifeld */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('murifeld', 'top', props)}
                onLeave={props => itemHandler('murifeld', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='murifeld'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('murifeld', 'bottom', props)}
                onLeave={props => itemHandler('murifeld', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Zaffaraya */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('zaffaraya', 'top', props)}
                onLeave={props => itemHandler('zaffaraya', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='zaffaraya'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('zaffaraya', 'bottom', props)}
                onLeave={props => itemHandler('zaffara<a', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Via Felsenau */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('viaFelsenau', 'top', props)}
                onLeave={props => itemHandler('viaFelsenau', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='viaFelsenau'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('viaFelsenau', 'bottom', props)}
                onLeave={props => itemHandler('viaFelsenau', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* WBG Aarestrand */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('aarestrand', 'top', props)}
                onLeave={props => itemHandler('aarestrand', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='aarestrand'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('aarestrand', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('aarestrand', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>

          {/*

            SECTION SOCIAL

          */}
          <div className='item' id={sections.SOCIAL}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.SOCIAL}`}
            />
            <h2 className='dividing ui infographic header'>Randständige</h2>
            <p>
              Leute, die sich zusammentun und gemeinsam Projekte lancieren: Das
              tönt alles sehr romantisch. Allerdings gehört es auch zur
              Geschichte der 80er-Bewegung, dass sie von der Heroin-Krise der
              80er-Jahre nicht unberührt blieb.
            </p>
            <p>
              Doch die Gesellschaft kannte bis dato nur zwei Formen des Umgangs
              mit Drogenabhängigen: Abstinenzorientierte Therapie und
              Repression. Es war die Szene selber, die neue Wege ging. So
              führten einige autonome Zentren schon früh Fixerräume ein, wo der
              Konsum akzeptiert wurde – teils mit verheerenden Auswirkungen auf
              die Zentren. Die Grundidee einer auf Schadensminderung abzielenden
              Drogenpolitik setzte sich aber durch.
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.SOCIAL}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* Sleeper */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('sleeper', 'top', props, 'first')}
                onLeave={props => itemHandler('sleeper', 'top', props, 'first')}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='sleeper'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('sleeper', 'bottom', props)}
                onLeave={props => itemHandler('sleeper', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Gassenarbeit */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('gassenarbeit', 'top', props)}
                onLeave={props => itemHandler('gassenarbeit', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='gassenarbeit'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('gassenarbeit', 'bottom', props)}
                onLeave={props => itemHandler('gassenarbeit', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Xenia */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('xenia', 'top', props)}
                onLeave={props => itemHandler('xenia', 'top', props)}
                topOffset={mapHeight + 'px'}
              />
              <Institution
                name='xenia'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('xenia', 'bottom', props)}
                onLeave={props => itemHandler('xenia', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              />

              {/* Fixerstübli */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('fixerstubli', 'top', props)}
                onLeave={props => itemHandler('fixerstubli', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='fixerstubli'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('fixerstubli', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('fixerstubli', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>

          {/*

            SECTION YOUTH

          */}
          <div className='item' id={sections.YOUTH}>
            <Tracking
              eventAction='vertical-scroll'
              eventLabel={`read-${sections.YOUTH}`}
            />
            <h2 className='dividing ui infographic header'>
              Kinder- und Jugendarbeit
            </h2>
            <p>
              Bereits die 1968er-Bewegung kritisierte die vorherrschende
              autoritäre Pädagogik. Die 80er-Bewegung führte diese Kritik
              weiter. In dieser Zeit entstanden zahlreiche Projekte für Kinder
              und Jugendliche, in denen man den Jungen auf Augenhöhe begegnen,
              ihnen die nötigen Freiräume zugestehen und sie zur
              Selbstständigkeit heranführen wollte.
            </p>
            <div className='leaflet-top-container'>
              <LeafletMap
                ref={refContainer}
                currentItems={item}
                currentSection={sections.YOUTH}
                currentPlace={place}
                updatePlace={updatePlace}
              />

              {/* Bronx */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('bronx', 'top', props, 'first')}
                onLeave={props => itemHandler('bronx', 'top', props, 'first')}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='bronx'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('bronx', 'bottom', props)}
                onLeave={props => itemHandler('bronx', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Juko */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('juko', 'top', props)}
                onLeave={props => itemHandler('juko', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='juko'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('juko', 'bottom', props)}
                onLeave={props => itemHandler('juko', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* Spielewerkstatt */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('spielewerkstatt', 'top', props)}
                onLeave={props => itemHandler('spielewerkstatt', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='spielewerkstatt'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('spielewerkstatt', 'bottom', props)
                }
                onLeave={props =>
                  itemHandler('spielewerkstatt', 'bottom', props)
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* schutzenweg */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('schutzenweg', 'top', props)}
                onLeave={props => itemHandler('schutzenweg', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='schutzenweg'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
                floatRight
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('schutzenweg', 'bottom', props)}
                onLeave={props => itemHandler('schutzenweg', 'bottom', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>

              {/* graffiti */}
              <Waypoint
                fireOnRapidScroll
                onEnter={props => itemHandler('graffiti', 'top', props)}
                onLeave={props => itemHandler('graffiti', 'top', props)}
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
              <Institution
                name='graffiti'
                updatePlace={updatePlace}
                currentPlace={place}
                isMobile={isMobile}
                currentItem={item}
              />
              <Waypoint
                fireOnRapidScroll
                onEnter={props =>
                  itemHandler('graffiti', 'bottom', props, 'last')
                }
                onLeave={props =>
                  itemHandler('graffiti', 'bottom', props, 'last')
                }
                topOffset={mapHeight + 'px'}
              >
                <span>{/* <hr /> */}</span>
              </Waypoint>
            </div>
          </div>
        </div>
      </div>
      <ArticleTeasers
        numberOfCards='two'
        articleIds={['345013327765', '358162212865']}
      />
      <Footer
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        mailTo='bern@derbund.ch'
        credits={[
          ['Text', 'Fabian Christl'],
          [
            'Foto',
            [
              ' Carmelo Agovino',
              ' Stefan Anderegg',
              ' Andreas Blatter',
              ' zvg/Anette Boutellier',
              ' Valérie Chételat',
              ' Laurent Crottet',
              ' Martin Guggisberg',
              ' zvg/Yoshiko Kusano',
              ' Beat Mathys',
              ' Adrian Moser',
              ' Walter Pfäffli',
              ' Ruben Wyttenbach'
            ]
          ],
          ['Digitale Umsetzung', 'Christian Zellweger'],
          ['Leitung Redaktion Bern', 'Marcello Odermatt']
        ]}
      />
    </div>
  )
}
